import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Payout, PayoutStatusEnum, Merchant } from '@parkupp/core'
import { PayoutService } from '../../services/payout.service'
import { MerchantService } from '../../services/merchant.service'
import { GlobalService } from 'src/app/services/global.service'
import * as firestore from 'firebase/firestore'

@Component({
    selector: 'app-merchant-dashboard-statements',
    templateUrl: './merchant-dashboard-statements.component.html',
    styleUrls: ['./merchant-dashboard-statements.component.scss'],
})
export class MerchantDashboardStatementsComponent implements OnInit, OnDestroy {
    merchantKey: string
    merchant: Merchant
    payouts: Payout[] = []
    paidPayouts: Payout[] = []
    owingPayouts: Payout[] = []

    fromDate: string
    toDate: string

    loading = false
    html2PdfAdded = false

    totalPaid = 0
    totalOwing = 0
    grandTotal = 0

    GlobalService = GlobalService
    PayoutStatusEnum = PayoutStatusEnum

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private merchantService: MerchantService,
        public payoutService: PayoutService
    ) {
        this.merchantKey = this.activatedRoute.snapshot.params['merchantKey']
    }

    ngOnInit(): void {
        // Hide WhatsApp button on this page
        this.hideWhatsAppButton()

        // Load merchant data
        this.loadMerchantData()

        // Set default date range to financial year (March 1 to today)
        const today = new Date()
        const currentYear = today.getFullYear()
        const currentMonth = today.getMonth() // 0-based

        // Financial year starts March 1
        // If we're in Jan or Feb, use previous year's March 1
        let financialYearStart: Date
        if (currentMonth < 2) {
            // 0=Jan, 1=Feb
            financialYearStart = new Date(currentYear - 1, 2, 1) // March 1 of previous year
        } else {
            financialYearStart = new Date(currentYear, 2, 1) // March 1 of current year
        }

        this.fromDate = this.formatDateForInput(financialYearStart)
        this.toDate = this.formatDateForInput(today)

        this.loadPayouts()
    }

    async loadMerchantData() {
        this.merchant = await this.merchantService.getMerchant(this.merchantKey)
    }

    hideWhatsAppButton() {
        const el = document.querySelector('.floating-help-container') as HTMLElement
        if (el) {
            el.style.display = 'none'
        }
    }

    ngOnDestroy(): void {
        const el = document.querySelector('.floating-help-container') as HTMLElement
        if (el) {
            el.style.display = ''
        }
    }

    formatDateForInput(date: Date): string {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    async loadPayouts() {
        this.loading = true

        const startDate = new Date(this.fromDate)
        startDate.setHours(0, 0, 0, 0)

        const endDate = new Date(this.toDate)
        endDate.setHours(23, 59, 59, 999)

        const merchantRef = firestore.doc(this.merchantService.firestore, 'merchants', this.merchantKey) as any

        this.payouts = await this.payoutService.getPayoutsByMerchantAndDateRange(merchantRef, startDate, endDate)

        this.payouts.sort((a, b) => {
            return b.createdAt.toDate().getTime() - a.createdAt.toDate().getTime()
        })

        // Separate paid and owing
        this.paidPayouts = this.payouts.filter((payout) => payout.status === PayoutStatusEnum.PAID)
        this.owingPayouts = this.payouts.filter((payout) => payout.status === PayoutStatusEnum.DUE || payout.status === PayoutStatusEnum.BATCHED)

        // Calculate totals
        this.totalPaid = this.paidPayouts.reduce((sum, payout) => sum + payout.payoutAmount, 0)
        this.totalOwing = this.owingPayouts.reduce((sum, payout) => sum + payout.payoutAmount, 0)
        this.grandTotal = this.totalPaid + this.totalOwing

        this.loading = false
    }

    onDateChange() {
        this.loadPayouts()
    }

    downloadPdf() {
        if (!this.html2PdfAdded) {
            this.html2PdfAdded = true
            const script = document.createElement('script')
            script.type = 'text/javascript'
            script.src = '//cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.2/html2pdf.bundle.min.js'
            script.onload = () => {
                this.generatePDF()
            }
            document.body.appendChild(script)
        } else {
            this.generatePDF()
        }
    }

    generatePDF() {
        // @ts-ignore
        if (typeof html2pdf !== 'function') {
            setTimeout(() => {
                this.generatePDF()
            }, 100)
            return
        }

        const element = document.getElementById('print-container')
        const filename = `statement-${this.fromDate}-to-${this.toDate}.pdf`

        // @ts-ignore
        html2pdf()
            .set({
                margin: [10, 10, 10, 10],
                filename: filename,
                html2canvas: {
                    scale: 1.5,
                    useCORS: true,
                    letterRendering: true,
                },
                jsPDF: {
                    unit: 'mm',
                    format: 'a4',
                    orientation: 'portrait',
                },
                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
            })
            .from(element)
            .save()
            .then(() => {
                this.changeDetectorRef.detectChanges()
            })
    }

    printPage() {
        window.print()
    }

    goBack() {
        this.router.navigate(['/merchant', this.merchantKey])
    }
}
